import { BaseComponentImpl } from '../types/implementations';
import type { PhotoLocation, PhotoModel } from '../types/protocols';

/**
 * Photo pins drawn with BlueprintWithMarkers. It reads a marker's position
 * from `blueprintLocations[currentBlueprintKey]`, so each pinned component is
 * wrapped with its pin under the photo's storage path, which also serves as
 * the "blueprint" key.
 */

/** The component wrapped so BlueprintWithMarkers finds `loc` under `blueprintLocations[loc.storagePath]`. */
export const photoMarkerComponent = (component: BaseComponentImpl, loc: PhotoLocation): BaseComponentImpl => {
    const wrapped: BaseComponentImpl = Object.create(component);
    wrapped.blueprintLocations = {
        [loc.storagePath]: {
            key: component.key,
            x: loc.x,
            y: loc.y,
            width: loc.width,
            height: loc.height,
        },
    };
    return wrapped;
};

/** A photo model for BlueprintWithMarkers built from a pin's photo fields. */
export const photoModelFromLocation = (loc: PhotoLocation): PhotoModel => ({
    key: loc.storagePath,
    storagePath: loc.storagePath,
    width: loc.width,
    height: loc.height,
    author: '',
    date: '',
} as PhotoModel);

/** One photo of a parent component with the components pinned in it. */
export interface ParentPhotoMarkers {
    photoKey: string;
    photoModel: PhotoModel;
    markerComponents: BaseComponentImpl[];
}

/**
 * The parent's photos that have pins of `components`, each with all of its
 * pinned components, newest photo first (photo keys are push keys). Pins in
 * other parents' photos are ignored. `pinnedKeys` holds the components shown
 * in at least one photo.
 */
export const parentPhotoMarkers = (
    parentKey: string,
    components: BaseComponentImpl[]
): { photos: ParentPhotoMarkers[]; pinnedKeys: Set<string> } => {
    const byPhoto = new Map<string, ParentPhotoMarkers>();
    const pinnedKeys = new Set<string>();

    components.forEach(component => {
        Object.entries(component.photoLocations ?? {}).forEach(([photoKey, loc]) => {
            if (!loc?.storagePath || loc.parentKey !== parentKey) return;
            let photo = byPhoto.get(photoKey);
            if (!photo) {
                photo = { photoKey, photoModel: photoModelFromLocation(loc), markerComponents: [] };
                byPhoto.set(photoKey, photo);
            }
            photo.markerComponents.push(photoMarkerComponent(component, loc));
            pinnedKeys.add(component.key);
        });
    });

    const photos = Array.from(byPhoto.values())
        .sort((a, b) => (a.photoKey < b.photoKey ? 1 : a.photoKey > b.photoKey ? -1 : 0));
    return { photos, pinnedKeys };
};
